import { set, isNaN } from 'lodash';
import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useYup from '../../hooks/useYup';
import useCurrentGroup from '../../hooks/useCurrentGroup';
import { useFlowsSelector } from '../../../../common/src/utils/globalVariables';
import { useFlowsRouteParams } from '../../hooks/useFlowsRouteParams';
import { hasSubGroups } from '../../config/flowsStructureConfig';
import useCurrentGroups from '../../components/ui/company-scan/hooks/useCurrentGroups';
import { FlowsQuestionGroup } from '../../types/FlowsTypes';

export interface FormValues {
  [fieldName: string]: unknown;
}

interface Props {
  groups?: FlowsQuestionGroup[];
  /** Used for manually specfying the index */
  currentIndex?: number;
}

const FlowsFormProvider: React.FC<React.PropsWithChildren<Props>> = ({
  groups,
  currentIndex,
  children,
}) => {
  const { flowId } = useFlowsRouteParams();
  const currentGroup = useCurrentGroup();
  const currentGroups = groups || useCurrentGroups();
  const answers = useFlowsSelector((state) => state.flows.answers);
  const isViewOnlyMode = useFlowsSelector(
    (state) => state.flows.isViewOnlyModeOn,
  );
  const questions =
    hasSubGroups(flowId) || !!groups
      ? currentGroups.map(({ questions }) => questions).flat()
      : currentGroup?.questions;

  // View only mode should have no error handling as this causes the flow to be blocked
  const schema = useYup(isViewOnlyMode ? [] : questions || []);

  const defaultValues = useMemo(() => {
    // Add all the values to an object like this
    // {
    //   policy_holder.first_name: "Test"
    // }

    // Takes the values that the questions of the current group might have (these come from BE or were manually added in FE)
    let objectWithValues = {};
    if (currentIndex == null)
      objectWithValues = Object.assign(
        {},
        ...(questions || [])
          .filter(({ value }) => value != null)
          .map(({ id, value }) => ({ [id]: value })),
      );

    // PUT THIS AFTER THE PREVIOUS ONE, SO THAT ANSWERS OVERWRITE QUESTION VALUES
    // Takes the default  values from the answers and puts them in an object for the current group
    Object.assign(
      objectWithValues,
      ...answers
        .filter(
          ({ groupId, tag }) =>
            groupId?.toLowerCase() === currentGroup?.id?.toLowerCase() ||
            groupId?.split('.')[0]?.toLowerCase() ===
              currentGroup?.id?.toLowerCase() ||
            tag?.toLowerCase() === currentGroup?.id.toLowerCase(),
        )
        .filter(({ index }) =>
          currentIndex != null
            ? index === currentIndex
            : currentGroup.index == null || index === currentGroup.index,
        )
        .map(({ id, answer }) => ({ [id]: isNaN(answer) ? null : answer })),
    );

    const formObject = {};

    // Puts the values above in a form object that is nested and suitable for react-hook-form
    // {
    //   policy_holder: {
    //     first_name: "Test"
    //   }
    // }
    Object.entries(objectWithValues).forEach(([key, value]) =>
      set(formObject, key, value),
    );

    return formObject;
  }, [currentGroup, answers]);

  const methods = useForm<FormValues>({
    defaultValues,
    ...(schema && { resolver: yupResolver(schema) }),
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export default FlowsFormProvider;
